import CheckIcon from "@mui/icons-material/Check";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataGridSettings } from "../DataGridSettingsProvider";
import ColorInterval from "@/models/ColorInterval";
import { DiagnosticSettingsSlotProps } from "@/models/CustomColumnMenuProps";

type EditableDateInterval = {
  minDate: string; // "YYYY-MM-DD"
  maxDate: string; // "YYYY-MM-DD"
  color: string;
  backgroundColor: string;
};

function timestampToDateString(ts: number): string {
  if (!isFinite(ts)) return "";
  return new Date(ts).toISOString().split("T")[0];
}

function colorIntervalToEditable(ci: ColorInterval): EditableDateInterval {
  return {
    minDate: timestampToDateString(ci.min),
    maxDate: timestampToDateString(ci.max),
    color: ci.color,
    backgroundColor: ci.backgroundColor,
  };
}

export default function DateIntervalMenu(props: DiagnosticSettingsSlotProps) {
  const { colorIntervals, setColorIntervals } = useDataGridSettings();
  const { t } = useTranslation();
  const field = props.field;

  const [localIntervals, setLocalIntervals] = useState<EditableDateInterval[]>(
    (colorIntervals[field] ?? []).map(colorIntervalToEditable),
  );
  const [isEditing, setIsEditing] = useState(false);

  const handleAddInterval = () => {
    setLocalIntervals(prev => [
      ...prev,
      { minDate: "", maxDate: "", color: "#000000", backgroundColor: "#ffffff" },
    ]);
    setIsEditing(true);
  };

  const handleUpdate = (
    index: number,
    key: keyof EditableDateInterval,
    value: string,
  ) => {
    setLocalIntervals(prev =>
      prev.map((interval, i) => (i === index ? { ...interval, [key]: value } : interval)),
    );
    setIsEditing(true);
  };

  const removeInterval = (index: number) => {
    setLocalIntervals(prev => prev.filter((_, i) => i !== index));
    setIsEditing(true);
  };

  const validateAndSave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (localIntervals.some(iv => !iv.minDate || !iv.maxDate)) {
      alert(t("faultOverview.dateIntervalValidation", "Start and end date must not be empty!"));
      return;
    }

    const intervals: ColorInterval[] = localIntervals.map(iv => ({
      min: new Date(iv.minDate).getTime(),
      max: new Date(iv.maxDate + "T23:59:59").getTime(),
      color: iv.color,
      backgroundColor: iv.backgroundColor,
    }));

    if (intervals.some(iv => iv.min > iv.max)) {
      alert(t("faultOverview.dateIntervalOrder", "Start date must be before end date!"));
      return;
    }

    intervals.sort((a, b) => a.min - b.min);

    for (let i = 0; i < intervals.length - 1; i++) {
      if (intervals[i].max > intervals[i + 1].min) {
        alert(t("faultOverview.dateIntervalOverlap", "Date intervals must not overlap!"));
        return;
      }
    }

    setColorIntervals(prev => ({ ...prev, [field]: intervals }));
    setIsEditing(false);
  };

  return (
    <div>
      <MenuItem onClick={handleAddInterval}>
        <ListItemIcon>
          <ColorLensIcon fontSize="small" />
        </ListItemIcon>
        <Typography>{t("faultOverview.colorIntervalsTitle")}</Typography>
        {isEditing && (
          <IconButton onClick={validateAndSave} color="success">
            <CheckIcon />
          </IconButton>
        )}
      </MenuItem>
      {localIntervals.map((interval, index) => (
        <MenuItem key={index} sx={{ gap: 1 }}>
          <IconButton onClick={() => removeInterval(index)}>
            <DeleteIcon />
          </IconButton>
          <TextField
            size="small"
            sx={{ width: "130px" }}
            type="date"
            value={interval.minDate}
            onKeyDown={e => e.stopPropagation()}
            onChange={e => handleUpdate(index, "minDate", e.target.value)}
          />
          <TextField
            size="small"
            sx={{ width: "130px" }}
            type="date"
            value={interval.maxDate}
            onKeyDown={e => e.stopPropagation()}
            onChange={e => handleUpdate(index, "maxDate", e.target.value)}
          />
          <TextField
            size="small"
            sx={{ width: "50px" }}
            type="color"
            value={interval.color}
            onChange={e => handleUpdate(index, "color", e.target.value)}
            title={t("faultOverview.value")}
          />
          <TextField
            size="small"
            sx={{ width: "50px" }}
            type="color"
            value={interval.backgroundColor}
            onChange={e => handleUpdate(index, "backgroundColor", e.target.value)}
            title={t("faultOverview.background")}
          />
        </MenuItem>
      ))}
    </div>
  );
}
